import { nanoid } from 'nanoid';
import { useMemo, type JSX } from 'react';
import { Box, Show, SimpleGrid, Text, useSlotRecipe } from '@chakra-ui/react';

import { Markdown } from '../Markdown';
import { descriptionRecipe } from './recipe';

interface IDescriptionProps {
  content?: string;
  filename?: string;
}

export function Description({
  content,
  filename,
}: IDescriptionProps): JSX.Element {
  const hasFilename = !!filename;
  const recipe = useSlotRecipe({
    recipe: descriptionRecipe,
  });

  const {
    markdown,
    container,
    markdownDesc,
    filenameContainer,
    markdownContainer,
    filename: filenameCss,
  } = recipe({
    variant: hasFilename ? 'file' : 'normal',
  });

  const formattedContent = useMemo(() => {
    if (!content) {
      return <></>;
    }

    return content.split('---').reduce<JSX.Element[]>((result, block) => {
      if (!block.trim()) {
        return result;
      }

      const [code, desc] = block.split('[description]');

      result.push(
        <SimpleGrid
          key={nanoid()}
          className='desc'
          css={markdownContainer}
          columns={{
            base: 1,
            md: 2,
          }}
        >
          <Markdown
            css={markdown}
            className='desc-code'
            content={code ? `${code.replace(/\[code\]/g, '```')}\n\`\`\`` : ''}
            context={{
              codeBlock: {
                wrapLongLines: true,
              },
            }}
          />
          <Text css={markdownDesc}>{desc || ''}</Text>
        </SimpleGrid>,
      );

      return result;
    }, []);
  }, [content, markdown, markdownContainer, markdownDesc]);

  return (
    <Box css={container}>
      <Show when={hasFilename}>
        <SimpleGrid
          columns={{
            base: 1,
            md: 2,
          }}
        >
          <Box css={filenameContainer}>
            <Text css={filenameCss}>{filename}</Text>
          </Box>
        </SimpleGrid>
      </Show>
      {formattedContent}
    </Box>
  );
}
